import React, { useCallback, useEffect, useRef } from "react";
import { ReactSVG } from "react-svg";
import { NewUsersStatus } from "src/assets/icons";

import * as Styled from "./styles";

interface BaseCardProps {
  id: string;
  header: React.ReactElement;
  actions?: React.ReactElement;
  details?: React.ReactElement;
  startExpanded?: boolean;
  inactive?: boolean;
  enableNewCard?: boolean;
  newCard?: boolean;
}
const BaseCard: React.FC<BaseCardProps> = ({
  id,
  header,
  actions,
  details,
  startExpanded = false,
  inactive = false,
  enableNewCard = false,
  newCard = false
}) => {
  const refCardDetails = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState<boolean>(startExpanded);

  // Effect to open the accordion
  useEffect(() => {
    if (expanded) {
      let childrenHeight = 15;

      [...(refCardDetails.current?.childNodes || [])].forEach(
        (child: ChildNode) => {
          childrenHeight += (child as HTMLElement).offsetHeight;
        }
      );

      if (refCardDetails?.current?.style) {
        refCardDetails.current.style.height = `${childrenHeight}px`;
      }
    } else if (refCardDetails?.current?.style) {
      refCardDetails.current.style.height = "0px";
    }
  }, [expanded]);

  useEffect(() => {
    if (inactive) setExpanded(startExpanded);
  }, [inactive, setExpanded]);

  const handleOpenClose = useCallback(() => {
    if (inactive) return;
    setExpanded(!expanded);
  }, [expanded, inactive]);

  return (
    <Styled.CardContainer id={`${id}`} inactive={inactive}>
      <Styled.CardSummary id={`${id}_summary`}>
        <Styled.CardHeader id={`${id}_header`} onClick={handleOpenClose}>
          {header}
        </Styled.CardHeader>
        {actions && (
          <Styled.CardActions id={`${id}_actions`}>
            { enableNewCard && (
              <Styled.IconNewCard newCard={newCard}>
                <ReactSVG src={NewUsersStatus} />
              </Styled.IconNewCard>
            )}
            <Styled.ContentAction enableNewCard={enableNewCard}>
              {actions}
            </Styled.ContentAction>
          </Styled.CardActions>
        )}
      </Styled.CardSummary>

      {details && (
        <>
          <Styled.CardDetails
            id={`${id}_details`}
            ref={refCardDetails}
            className="cardDetails"
          >
            {details}
          </Styled.CardDetails>
          <Styled.CardToggleButtonContainer>
            <Styled.CardToggleButton
              className="vehicle-summary__collapse-icon"
              onKeyPress={handleOpenClose}
              role="button"
              aria-label={`${id}_open_card`}
              tabIndex={0}
              onClick={handleOpenClose}
              expanded={expanded && !inactive}
            />
          </Styled.CardToggleButtonContainer>
        </>
      )}
    </Styled.CardContainer>
  );
};

BaseCard.defaultProps = {
  actions: undefined,
  details: undefined,
  startExpanded: false,
  inactive: false,
  enableNewCard: false,
  newCard: false
};

export default BaseCard;
